import { BaseAPIService } from '../api/base';
import { supabase } from '../api/client';

/**
 * MEE (Multistate Essay Examination) Service
 * Handles all MEE exam-related API calls
 */
class MEEService extends BaseAPIService {
  constructor() {
    super('mee_questions');
  }

  /**
   * Get MEE questions
   */
  async getQuestions(options = {}) {
    try {
      let query = supabase
        .from('mee_questions')
        .select(options.select || '*');

      if (options.subjectId) {
        query = query.eq('subject_id', options.subjectId);
      }

      query = query.order('created_at', { ascending: false });

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MEE questions:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save MEE answer
   */
  async saveAnswer(userId, questionId, answer) {
    try {
      const { data, error } = await supabase
        .from('mee_answers')
        .insert([
          {
            user_id: userId,
            question_id: questionId,
            answer_text: answer,
            submitted_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving MEE answer:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save draft answer
   */
  async saveDraft(userId, questionId, draftContent) {
    try {
      const { data, error } = await supabase
        .from('mee_drafts')
        .upsert([
          {
            user_id: userId,
            question_id: questionId,
            draft_content: draftContent,
            updated_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving MEE draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get draft answer
   */
  async getDraft(userId, questionId) {
    try {
      const { data, error } = await supabase
        .from('mee_drafts')
        .select('*')
        .eq('user_id', userId)
        .eq('question_id', questionId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MEE draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Request AI evaluation
   */
  async requestEvaluation(userId, answerId) {
    try {
      const { data, error } = await supabase
        .from('mee_evaluations')
        .insert([
          {
            user_id: userId,
            answer_id: answerId,
            status: 'pending',
            requested_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) throw error;
      
      // Trigger AI evaluation (this would call an edge function or external API)
      // For now, we'll just return the pending evaluation
      
      return { success: true, data };
    } catch (error) {
      console.error('Error requesting MEE evaluation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get evaluation results
   */
  async getEvaluation(evaluationId) {
    try {
      const { data, error } = await supabase
        .from('mee_evaluations')
        .select('*')
        .eq('id', evaluationId)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching MEE evaluation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user progress
   */
  async getUserProgress(userId) {
    try {
      const { data, error } = await supabase
        .from('mee_answers')
        .select('*, mee_evaluations(*)')
        .eq('user_id', userId);

      if (error) throw error;

      const stats = {
        totalAnswers: data.length,
        evaluatedAnswers: data.filter(a => a.mee_evaluations?.length > 0).length,
        averageScore: 0,
      };

      // Calculate average score from evaluations
      const scores = data
        .flatMap(a => a.mee_evaluations || [])
        .filter(e => e.score !== null)
        .map(e => e.score);

      if (scores.length > 0) {
        stats.averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
      }

      return { success: true, data: { answers: data, stats } };
    } catch (error) {
      console.error('Error fetching MEE progress:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const meeService = new MEEService();
export default meeService;